import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, CheckCheck, Zap, Info, TrendingDown, TrendingUp, ShieldAlert, AlertTriangle, Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import api from '@/lib/api';
import logger from '@/lib/logger';

const SEVERITY_STYLE = {
  // Bearish family (rose / amber / sky by severity).
  bearish_high:   { bg: 'bg-rose-950/60',    text: 'text-rose-300',    border: 'border-rose-800/50' },
  bearish_medium: { bg: 'bg-amber-950/60',   text: 'text-amber-300',   border: 'border-amber-800/40' },
  bearish_low:    { bg: 'bg-sky-950/60',     text: 'text-sky-300',     border: 'border-sky-800/40' },
  // Bullish family (all-emerald with severity-scaled intensity).
  bullish_high:   { bg: 'bg-emerald-950/60', text: 'text-emerald-300', border: 'border-emerald-800/50' },
  bullish_medium: { bg: 'bg-emerald-950/40', text: 'text-emerald-400', border: 'border-emerald-800/30' },
  bullish_low:    { bg: 'bg-emerald-950/20', text: 'text-emerald-500', border: 'border-emerald-800/20' },
  // Legacy fallback for pre-sentiment notifications.
  high:   { bg: 'bg-rose-950/60',    text: 'text-rose-300',    border: 'border-rose-800/50' },
  medium: { bg: 'bg-amber-950/60',   text: 'text-amber-300',   border: 'border-amber-800/40' },
  low:    { bg: 'bg-sky-950/60',     text: 'text-sky-300',     border: 'border-sky-800/40' },
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await api.get('/notifications?limit=15');
      setNotifications(response.data?.notifications || []);
      setUnreadCount(response.data?.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Native browser notification banner for newest unread item — fired once
  // per id to avoid re-flashing on every poll.
  const lastShownNotifIdRef = useRef(null);
  useEffect(() => {
    if (unreadCount > 0 && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      const latest = notifications.find((n) => !n.is_read);
      if (latest && latest.id !== lastShownNotifIdRef.current) {
        lastShownNotifIdRef.current = latest.id;
        try {
          new Notification('dotbox', { body: latest.message, icon: '/favicon.ico', tag: latest.id });
        } catch (e) {
          console.error('Browser notification failed:', e);
        }
      }
    }
  }, [unreadCount, notifications]);

  const requestBrowserPermission = async () => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      // iter86 — stable toast id so a re-triggered permission prompt never
      // stacks multiple identical banners.
      if (permission === 'granted') {
        toast.success('Browser notifications enabled', { id: 'browser-notif-granted' });
      }
    }
  };

  // iter87 — Refetch immediately after any write so local state can never
  // drift from the server. Callers await this to guarantee the badge is
  // reconciled before subsequent UI actions.
  const handleMarkRead = async (id) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
      await fetchNotifications();  // reconcile with server
    } catch (err) {
      console.error('Notification mark-read failed:', err);
      toast.error('Failed to mark as read', { id: 'notif-mark-read-failed' });
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.post('/notifications/mark-all-read');
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
      await fetchNotifications();  // reconcile with server
      toast.success('All notifications marked as read', { id: 'notif-mark-all-read-ok' });
    } catch (err) {
      console.error('Notification mark-all-read failed:', err);
      toast.error('Failed to mark all as read', { id: 'notif-mark-all-read-fail' });
    }
  };

  const formatTime = (dateStr) => {
    try {
      const date = new Date(dateStr);
      const diffMs = Date.now() - date.getTime();
      const diffMin = Math.floor(diffMs / 60000);
      const diffHr = Math.floor(diffMs / 3600000);
      const diffDay = Math.floor(diffMs / 86400000);
      if (diffMin < 1) return 'Just now';
      if (diffMin < 60) return `${diffMin}m ago`;
      if (diffHr < 24) return `${diffHr}h ago`;
      if (diffDay < 7) return `${diffDay}d ago`;
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch (err) {
      logger.debug('formatTime failed:', err);
      return '';
    }
  };

  const getIcon = (n) => {
    const type = n?.type;
    if (type === 'economist_alert') {
      const rule = n?.metadata?.rule || '';
      const sentiment = n?.metadata?.sentiment || 'bearish';
      const tone = sentiment === 'bullish' ? 'text-emerald-400' : 'text-rose-400';
      // Bullish sub-rules
      if (rule === 'high_conviction_bullish') return <Sparkles className={`h-4 w-4 ${tone}`} />;
      if (rule === 'upside_scenario_threshold') return <TrendingUp className={`h-4 w-4 ${tone}`} />;
      // Bearish sub-rules
      if (rule === 'redteam_bearish_challenge') return <ShieldAlert className={`h-4 w-4 ${tone}`} />;
      if (rule === 'high_conviction_bearish')   return <AlertTriangle className={`h-4 w-4 ${tone}`} />;
      if (rule === 'downside_scenario_threshold') return <TrendingDown className={`h-4 w-4 ${tone}`} />;
      // Unknown rule but still an economist alert — colour by sentiment.
      return sentiment === 'bullish'
        ? <TrendingUp className={`h-4 w-4 ${tone}`} />
        : <TrendingDown className={`h-4 w-4 ${tone}`} />;
    }
    switch (type) {
      case 'signal_results': return <Zap className="h-4 w-4 text-red-400" />;
      case 'system': return <Info className="h-4 w-4 text-gray-500" />;
      default: return <Bell className="h-4 w-4 text-gray-500" />;
    }
  };

  const routeFor = (n) => {
    // Explicit deep_link wins (Economist alerts include one that points to the
    // exact source analysis in the Saved tab).
    const dl = n?.metadata?.deep_link;
    if (dl) return dl;
    if (n?.type === 'economist_alert' && n?.metadata?.run_id) {
      return `/dashboard/economist?tab=saved&run=${n.metadata.run_id}`;
    }
    if (n?.type === 'signal_results' && n?.signal_id) return '/dashboard/signals';
    return '/dashboard';
  };

  // iter86 — Await the mark-read PUT before navigating, and use react-router
  // `navigate()` instead of `window.location.href`. Fixes both the "Failed
  // to mark as read" toast (from PUT cancellation) and the stale unread
  // count that persisted after a full page reload.
  const handleItemClick = async (notification) => {
    const target = routeFor(notification);
    if (!notification.is_read) {
      await handleMarkRead(notification.id);
    }
    setOpen(false);
    navigate(target);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative h-10 w-10 rounded-full text-white hover:bg-[#DC2626]"
          onClick={() => { setOpen(!open); requestBrowserPermission(); }}
          data-testid="notification-bell"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 h-5 w-5 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center font-medium"
              data-testid="notification-bell-count"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end" sideOffset={8}>
        <div className="flex items-center justify-between p-3 border-b">
          <h3 className="font-semibold text-sm">Notifications</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto p-1 text-xs text-red-400 hover:text-slate-100"
              onClick={handleMarkAllRead}
              data-testid="notification-mark-all-read"
            >
              <CheckCheck className="h-3 w-3 mr-1" />
              Mark all read
            </Button>
          )}
        </div>

        {/* iter87 — Popover height sized to comfortably fit ~5 full rows
            (~118px each) with visible scrollbar for the rest. Previous
            `max-h-[26rem]` (416px) clipped the 4th row at ~570px viewport.
            Now uses viewport-fluid clamping so it also adapts to short
            windows (e.g. laptop screens with the browser dev tools open). */}
        <ScrollArea className="max-h-[min(72vh,36rem)]">
          {notifications.length === 0 ? (
            <div className="p-6 text-center">
              <Bell className="h-8 w-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No notifications yet</p>
              <p className="text-xs text-gray-400 mt-1">
                Notifications appear when signals fire or Economist alerts trigger.
              </p>
            </div>
          ) : (
            <div className="pb-2">
              {notifications.map((notification) => {
                const severity = notification?.metadata?.severity;
                const sentiment = notification?.metadata?.sentiment;
                // Prefer sentiment_severity key; fall back to severity-only legacy.
                const sevKey = sentiment && severity ? `${sentiment}_${severity}` : severity;
                const sev = SEVERITY_STYLE[sevKey] || SEVERITY_STYLE[severity] || null;
                const pillLabel = sentiment ? `${severity} · ${sentiment}` : severity;
                return (
                  <div
                    key={notification.id}
                    className={`flex items-start gap-3 p-3 hover:bg-[#0A1024] cursor-pointer border-b border-[#1F2A4D] last:border-0 ${
                      !notification.is_read ? 'bg-[#DC2626]/5' : ''
                    }`}
                    onClick={() => handleItemClick(notification)}
                    data-testid={`notification-item-${notification.id}`}
                  >
                    <div className="flex-shrink-0 mt-0.5">{getIcon(notification)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm leading-tight ${!notification.is_read ? 'font-semibold text-slate-100' : 'text-slate-200'}`}>
                          {notification.title}
                        </p>
                        {!notification.is_read && <div className="h-2 w-2 rounded-full bg-[#DC2626] flex-shrink-0 mt-1.5" />}
                      </div>
                      {sev && (
                        <span
                          className={`inline-block mt-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${sev.bg} ${sev.text} border ${sev.border}`}
                          data-testid={`notification-severity-${sentiment ? sentiment + '-' : ''}${severity}`}
                        >
                          {pillLabel}
                        </span>
                      )}
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">{notification.message}</p>
                      <p className="text-xs text-gray-400 mt-1">{formatTime(notification.created_at)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {notifications.length > 0 && (
          <div className="p-2 border-t text-center">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-gray-500 h-auto p-1"
              onClick={() => { setOpen(false); navigate('/dashboard/settings'); }}
            >
              Notification Settings
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
